import React, {useState, useEffect} from 'react'
import {getAdminToken} from '../auth/_requests'

const API_URL = process.env.REACT_APP_API_URL

interface AdminUserItem {
  _id: string
  username: string
  email: string
  first_name: string
  last_name: string
  is_active: boolean
  is_admin: boolean
  is_super_admin: boolean
  is_deleted?: boolean
  deleted_at?: string
  last_login?: string
  last_login_attempt?: string
  created_at?: string
  permissions?: {
    can_modify_plan: boolean
    can_edit_accounts: boolean
  }
}

const colors = {
  bgPrimary: '#f5f8fa',
  bgSecondary: '#ffffff',
  bgTertiary: '#e8ecef',
  bgHover: '#e1e5ea',
  textPrimary: '#1e1e2d',
  textSecondary: '#5e6278',
  textMuted: '#a1a5b7',
  accentPrimary: '#3699ff',
  accentSuccess: '#1bc5bd',
  accentWarning: '#ffa800',
  accentDanger: '#f64e60',
  borderColor: 'rgba(0, 0, 0, 0.08)',
}

function getHeaders(): HeadersInit {
  const token = getAdminToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? {Authorization: `Bearer ${token}`} : {}),
  }
}

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUserItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)

  // Create user modal
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    is_admin: true,
    is_super_admin: false,
  })
  const [createLoading, setCreateLoading] = useState(false)

  // Reset password modal
  const [resetUserId, setResetUserId] = useState<string | null>(null)
  const [resetPassword, setResetPassword] = useState('')
  const [resetLoading, setResetLoading] = useState(false)

  // Edit modal
  const [editUser, setEditUser] = useState<AdminUserItem | null>(null)
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
  })
  const [editLoading, setEditLoading] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/admin/admin-users`, {
        headers: getHeaders(),
        credentials: 'include',
      })
      const data = await res.json()
      if (data.success) {
        setUsers(data.users)
      } else {
        setError(data.message || 'Failed to load users')
      }
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const toggleField = async (userId: string, field: 'is_active' | 'is_admin' | 'is_super_admin', currentValue: boolean) => {
    try {
      const res = await fetch(`${API_URL}/admin/admin-users/${userId}`, {
        method: 'PUT',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({[field]: !currentValue}),
      })
      const data = await res.json()
      if (data.success) {
        setUsers((prev) => prev.map((u) => (u._id === userId ? {...u, [field]: !currentValue} : u)))
      }
    } catch (e) {
      console.error('Toggle failed:', e)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreateLoading(true)
    try {
      const res = await fetch(`${API_URL}/admin/admin-users`, {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify(createForm),
      })
      const data = await res.json()
      if (data.success) {
        setShowCreate(false)
        setCreateForm({username: '', email: '', password: '', first_name: '', last_name: '', is_admin: true, is_super_admin: false})
        loadUsers()
      } else {
        alert(data.message || 'Failed to create user')
      }
    } catch (e: any) {
      alert(e.message)
    } finally {
      setCreateLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!resetUserId) return
    setResetLoading(true)
    try {
      const res = await fetch(`${API_URL}/admin/admin-users/${resetUserId}/reset-password`, {
        method: 'PUT',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({password: resetPassword}),
      })
      const data = await res.json()
      if (data.success) {
        setResetUserId(null)
        setResetPassword('')
        alert('Password reset successfully')
      } else {
        alert(data.message || 'Failed to reset password')
      }
    } catch (e: any) {
      alert(e.message)
    } finally {
      setResetLoading(false)
    }
  }

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editUser) return
    setEditLoading(true)
    try {
      const res = await fetch(`${API_URL}/admin/admin-users/${editUser._id}`, {
        method: 'PUT',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify(editForm),
      })
      const data = await res.json()
      if (data.success) {
        setEditUser(null)
        loadUsers()
      } else {
        alert(data.message || 'Failed to update user')
      }
    } catch (e: any) {
      alert(e.message)
    } finally {
      setEditLoading(false)
    }
  }

  const handleDelete = async (userId: string, username: string) => {
    if (!window.confirm(`Are you sure you want to delete ${username}?`)) return
    try {
      const res = await fetch(`${API_URL}/admin/admin-users/${userId}`, {
        method: 'DELETE',
        headers: getHeaders(),
        credentials: 'include',
      })
      const data = await res.json()
      if (data.success) {
        loadUsers()
      } else {
        alert(data.message || 'Failed to delete user')
      }
    } catch (e: any) {
      alert(e.message)
    }
  }

  const handleSoftDelete = async (userId: string, username: string, isDeleted: boolean) => {
    const action = isDeleted ? 'שחזר' : 'השבת'
    if (!window.confirm(`${action} את המשתמש ${username}?`)) return
    try {
      const res = await fetch(`${API_URL}/admin/admin-users/${userId}/soft-delete`, {
        method: 'PUT',
        headers: getHeaders(),
        credentials: 'include',
      })
      const data = await res.json()
      if (data.success) {
        setUsers((prev) => prev.map((u) => (u._id === userId ? {...u, is_deleted: data.user.is_deleted, deleted_at: data.user.deleted_at} : u)))
      } else {
        alert(data.message || 'Failed')
      }
    } catch (e: any) {
      alert(e.message)
    }
  }

  const openEdit = (user: AdminUserItem) => {
    setEditUser(user)
    setEditForm({
      username: user.username,
      email: user.email,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
    })
  }

  const formatDate = (date?: string) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <div style={{padding: 40, textAlign: 'center'}}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{padding: 40, textAlign: 'center', color: colors.accentDanger}}>
        <i className='bi bi-exclamation-triangle' style={{fontSize: 24}}></i>
        <p>{error}</p>
      </div>
    )
  }

  return (
    <div style={{padding: '24px 32px', direction: 'rtl'}}>
      {/* Header */}
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24}}>
        <div>
          <h2 style={{margin: 0, color: colors.textPrimary, fontSize: 22, fontWeight: 600}}>
            <i className='bi bi-shield-lock' style={{marginLeft: 8}}></i>
            ניהול משתמשי אדמין
          </h2>
          <p style={{margin: '4px 0 0', color: colors.textMuted, fontSize: 13}}>
            {users.length} משתמשים
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            background: colors.accentPrimary,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '10px 20px',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <i className='bi bi-plus-lg'></i>
          הוסף משתמש
        </button>
      </div>

      {/* Table */}
      <div
        style={{
          background: colors.bgSecondary,
          borderRadius: 12,
          border: `1px solid ${colors.borderColor}`,
          overflow: 'hidden',
        }}
      >
        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
          <thead>
            <tr
              style={{
                background: colors.bgTertiary,
                borderBottom: `1px solid ${colors.borderColor}`,
              }}
            >
              <th style={thStyle}>שם</th>
              <th style={thStyle}>שם משתמש</th>
              <th style={thStyle}>אימייל</th>
              <th style={{...thStyle, textAlign: 'center'}}>פעיל</th>
              <th style={{...thStyle, textAlign: 'center'}}>אדמין</th>
              <th style={{...thStyle, textAlign: 'center'}}>סופר אדמין</th>
              <th style={thStyle}>התחברות אחרונה</th>
              <th style={thStyle}>נסיון אחרון</th>
              <th style={{...thStyle, textAlign: 'center'}}>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr
                key={user._id}
                style={{
                  borderBottom: `1px solid ${colors.borderColor}`,
                  background: user.is_deleted ? '#fff0f0' : hoveredRow === user._id ? colors.bgHover : 'transparent',
                  opacity: user.is_deleted ? 0.6 : 1,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={() => setHoveredRow(user._id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td style={tdStyle}>
                  <span style={{fontWeight: 500, color: user.is_deleted ? colors.accentDanger : colors.textPrimary}}>
                    {user.first_name} {user.last_name}
                    {user.is_deleted && <span style={{fontSize: 11, marginRight: 6, color: colors.accentDanger, fontWeight: 400}}>(מושבת)</span>}
                  </span>
                </td>
                <td style={tdStyle}>
                  <code style={{fontSize: 13, color: colors.textSecondary}}>{user.username}</code>
                </td>
                <td style={tdStyle}>
                  <span style={{color: colors.textSecondary, fontSize: 13}}>{user.email}</span>
                </td>
                <td style={{...tdStyle, textAlign: 'center'}}>
                  <ToggleBadge
                    active={user.is_active}
                    activeLabel='פעיל'
                    inactiveLabel='לא פעיל'
                    activeColor={colors.accentSuccess}
                    inactiveColor={colors.accentDanger}
                    onClick={() => toggleField(user._id, 'is_active', user.is_active)}
                  />
                </td>
                <td style={{...tdStyle, textAlign: 'center'}}>
                  <ToggleBadge
                    active={user.is_admin}
                    activeLabel='אדמין'
                    inactiveLabel='קליינט'
                    activeColor={colors.accentPrimary}
                    inactiveColor={colors.textMuted}
                    onClick={() => toggleField(user._id, 'is_admin', user.is_admin)}
                  />
                </td>
                <td style={{...tdStyle, textAlign: 'center'}}>
                  <ToggleBadge
                    active={user.is_super_admin}
                    activeLabel='סופר'
                    inactiveLabel='-'
                    activeColor={colors.accentWarning}
                    inactiveColor={colors.bgTertiary}
                    onClick={() => toggleField(user._id, 'is_super_admin', user.is_super_admin)}
                  />
                </td>
                <td style={tdStyle}>
                  <span style={{color: colors.textMuted, fontSize: 12}}>{formatDate(user.last_login)}</span>
                </td>
                <td style={tdStyle}>
                  <span style={{color: colors.textMuted, fontSize: 12}}>{formatDate(user.last_login_attempt)}</span>
                </td>
                <td style={{...tdStyle, textAlign: 'center'}}>
                  <div style={{display: 'flex', gap: 4, justifyContent: 'center'}}>
                    <ActionBtn
                      icon='bi-pencil'
                      title='ערוך'
                      color={colors.accentPrimary}
                      onClick={() => openEdit(user)}
                    />
                    <ActionBtn
                      icon='bi-key'
                      title='איפוס סיסמה'
                      color={colors.accentWarning}
                      onClick={() => {
                        setResetUserId(user._id)
                        setResetPassword('')
                      }}
                    />
                    <ActionBtn
                      icon={user.is_deleted ? 'bi-arrow-counterclockwise' : 'bi-person-x'}
                      title={user.is_deleted ? 'שחזר משתמש' : 'השבת משתמש'}
                      color={user.is_deleted ? colors.accentSuccess : colors.accentDanger}
                      onClick={() => handleSoftDelete(user._id, user.username, !!user.is_deleted)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title='הוסף משתמש אדמין'>
          <form onSubmit={handleCreate}>
            <FormRow label='שם פרטי'>
              <input
                style={inputStyle}
                value={createForm.first_name}
                onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})}
              />
            </FormRow>
            <FormRow label='שם משפחה'>
              <input
                style={inputStyle}
                value={createForm.last_name}
                onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})}
              />
            </FormRow>
            <FormRow label='שם משתמש *'>
              <input
                style={inputStyle}
                required
                value={createForm.username}
                onChange={(e) => setCreateForm({...createForm, username: e.target.value})}
              />
            </FormRow>
            <FormRow label='אימייל *'>
              <input
                style={inputStyle}
                required
                type='email'
                value={createForm.email}
                onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
              />
            </FormRow>
            <FormRow label='סיסמה *'>
              <input
                style={inputStyle}
                required
                type='password'
                value={createForm.password}
                onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
              />
            </FormRow>
            <div style={{display: 'flex', gap: 16, margin: '12px 0'}}>
              <label style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer'}}>
                <input
                  type='checkbox'
                  checked={createForm.is_admin}
                  onChange={(e) => setCreateForm({...createForm, is_admin: e.target.checked})}
                />
                אדמין
              </label>
              <label style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer'}}>
                <input
                  type='checkbox'
                  checked={createForm.is_super_admin}
                  onChange={(e) => setCreateForm({...createForm, is_super_admin: e.target.checked})}
                />
                סופר אדמין
              </label>
            </div>
            <div style={{display: 'flex', gap: 8, justifyContent: 'flex-start', marginTop: 16}}>
              <button
                type='submit'
                disabled={createLoading}
                style={{
                  ...btnPrimary,
                  opacity: createLoading ? 0.6 : 1,
                }}
              >
                {createLoading ? 'יוצר...' : 'צור משתמש'}
              </button>
              <button type='button' onClick={() => setShowCreate(false)} style={btnSecondary}>
                ביטול
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reset Password Modal */}
      {resetUserId && (
        <Modal onClose={() => setResetUserId(null)} title='איפוס סיסמה'>
          <form onSubmit={handleResetPassword}>
            <FormRow label='סיסמה חדשה'>
              <input
                style={inputStyle}
                required
                type='password'
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                minLength={4}
              />
            </FormRow>
            <div style={{display: 'flex', gap: 8, justifyContent: 'flex-start', marginTop: 16}}>
              <button
                type='submit'
                disabled={resetLoading}
                style={{
                  ...btnPrimary,
                  background: colors.accentWarning,
                  opacity: resetLoading ? 0.6 : 1,
                }}
              >
                {resetLoading ? 'מאפס...' : 'אפס סיסמה'}
              </button>
              <button type='button' onClick={() => setResetUserId(null)} style={btnSecondary}>
                ביטול
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <Modal onClose={() => setEditUser(null)} title={`ערוך: ${editUser.username}`}>
          <form onSubmit={handleEdit}>
            <FormRow label='שם פרטי'>
              <input
                style={inputStyle}
                value={editForm.first_name}
                onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
              />
            </FormRow>
            <FormRow label='שם משפחה'>
              <input
                style={inputStyle}
                value={editForm.last_name}
                onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
              />
            </FormRow>
            <FormRow label='שם משתמש'>
              <input
                style={inputStyle}
                required
                value={editForm.username}
                onChange={(e) => setEditForm({...editForm, username: e.target.value})}
              />
            </FormRow>
            <FormRow label='אימייל'>
              <input
                style={inputStyle}
                required
                type='email'
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
              />
            </FormRow>
            <div style={{display: 'flex', gap: 8, justifyContent: 'flex-start', marginTop: 16}}>
              <button
                type='submit'
                disabled={editLoading}
                style={{
                  ...btnPrimary,
                  opacity: editLoading ? 0.6 : 1,
                }}
              >
                {editLoading ? 'שומר...' : 'שמור'}
              </button>
              <button type='button' onClick={() => setEditUser(null)} style={btnSecondary}>
                ביטול
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

// --- Sub-components ---

const ToggleBadge: React.FC<{
  active: boolean
  activeLabel: string
  inactiveLabel: string
  activeColor: string
  inactiveColor: string
  onClick: () => void
}> = ({active, activeLabel, inactiveLabel, activeColor, inactiveColor, onClick}) => (
  <span
    onClick={onClick}
    style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 500,
      cursor: 'pointer',
      background: active ? activeColor + '20' : inactiveColor + '20',
      color: active ? activeColor : inactiveColor,
      border: `1px solid ${active ? activeColor + '40' : inactiveColor + '30'}`,
      transition: 'all 0.2s',
      userSelect: 'none',
    }}
  >
    {active ? activeLabel : inactiveLabel}
  </span>
)

const ActionBtn: React.FC<{
  icon: string
  title: string
  color: string
  onClick: () => void
}> = ({icon, title, color, onClick}) => (
  <button
    title={title}
    onClick={onClick}
    style={{
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px 6px',
      borderRadius: 4,
      color: color,
      fontSize: 15,
      opacity: 0.7,
      transition: 'opacity 0.15s',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
    onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
  >
    <i className={`bi ${icon}`}></i>
  </button>
)

const Modal: React.FC<{
  onClose: () => void
  title: string
  children: React.ReactNode
}> = ({onClose, title, children}) => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}
    onClick={onClose}
  >
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: '24px 28px',
        minWidth: 400,
        maxWidth: 500,
        direction: 'rtl',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <h3 style={{margin: '0 0 20px', fontSize: 18, fontWeight: 600}}>{title}</h3>
      {children}
    </div>
  </div>
)

const FormRow: React.FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div style={{marginBottom: 12}}>
    <label style={{display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#5e6278'}}>
      {label}
    </label>
    {children}
  </div>
)

// --- Styles ---

const thStyle: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'right',
  fontSize: 12,
  fontWeight: 600,
  color: '#5e6278',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
}

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  verticalAlign: 'middle',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: 6,
  fontSize: 14,
  outline: 'none',
  direction: 'ltr',
}

const btnPrimary: React.CSSProperties = {
  background: '#3699ff',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '8px 20px',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
}

const btnSecondary: React.CSSProperties = {
  background: '#e8ecef',
  color: '#5e6278',
  border: 'none',
  borderRadius: 6,
  padding: '8px 20px',
  fontSize: 14,
  cursor: 'pointer',
}

export default AdminUsersPage
